import { useMemo } from 'react';

import { getBrandingService } from 'services/preload';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { isAuthed } from 'utils/common/session';

import { useUserInfo } from 'hooks/auth/useUserInfo';

const brandingQueryKeys = (userId) => ['branding-navigation', parseInt(userId)];

const fetchBrandingData = async () => {
  const response = await getBrandingService();
  if (!response?.isSuccess) {
    throw new Error(response?.message || 'Failed to fetch branding data');
  }
  return response;
};

export const invalidateBrandingNavigation = (
  queryClient,
  userIdProp = 'anonymous'
) => {
  queryClient.invalidateQueries({
    queryKey: brandingQueryKeys(userIdProp),
  });
};

export const useQueryBrandingNavigation = ({ userId: userIdProp } = {}) => {
  const { userId } = useUserInfo();
  const queryClient = useQueryClient();

  const isAuthenticated = isAuthed(); // Add auth check

  const query = useQuery({
    queryKey: brandingQueryKeys(userIdProp ?? userId),
    queryFn: fetchBrandingData,
    enabled: isAuthenticated && (!!userIdProp || !!userId), // ✅ Only run if authenticated
  });

  return {
    brandingNavigation: useMemo(() => query?.data?.data, [query?.data]) ?? {},
    invalidateBrandingNavigation: () =>
      invalidateBrandingNavigation(queryClient, userIdProp ?? userId),
  };
};

export const queryBrandingNavigation = ({ queryClient, userId }) => {
  return queryClient.ensureQueryData({
    queryKey: brandingQueryKeys(userId),
    queryFn: fetchBrandingData,
  });
};
